import React, { useState } from 'react';
import clsx from 'clsx';
import styles from './ProjectCardLabels.module.css';

/**
 * Label data structure
 */
export interface Label {
  /** Unique label identifier */
  id: string;
  /** Label text content */
  text: string;
  /** Optional background color (currently not used in styling) */
  color?: string;
}

/**
 * Props for ProjectCardLabels component
 */
interface ProjectCardLabelsProps {
  /** Array of labels to display (can be Label objects or strings) */
  labels: (Label | string)[];
  /** If true, removes horizontal padding (for list view alignment) */
  noPadding?: boolean;
  /** Optional data-test attribute for testing */
  'data-test'?: string;
}

/**
 * ProjectCardLabels - Displays project labels with truncation and overflow handling
 * Shows first 4 labels, displays "+x" indicator for remaining labels with hover popover
 * Used by both ProjectCard and ProjectList components
 */
export const ProjectCardLabels: React.FC<ProjectCardLabelsProps> = ({ labels, noPadding = false, 'data-test': dataTest }) => {
  const [showLabelPopover, setShowLabelPopover] = useState(false);
  
  // Normalize labels to ensure they have id and text properties
  const normalizedLabels = labels.map((label, index) => {
    if (typeof label === 'string') {
      return { id: `label-${index}`, text: label };
    }
    return label;
  });
  
  // Show first 4 labels, hide the rest
  const visibleLabels = normalizedLabels.slice(0, 4);
  const remainingCount = normalizedLabels.length - 4;
  const remainingLabels = normalizedLabels.slice(4);

  // Don't render if no labels
  if (labels.length === 0) {
    return null;
  }

  return (
    <div
      data-test={dataTest}
      className={clsx(
        styles.labelsContainer,
        noPadding && styles.labelsContainerNoPadding
      )}
    >
      {/* Visible labels (first 4) */}
      {visibleLabels.map((label) => (
        <span
          key={label.id}
          className={styles.label}
          title={label.text}
        >
          {label.text}
        </span>
      ))}
      
      {/* "+x" indicator with popover for remaining labels */}
      {remainingCount > 0 && (
        <span 
          className={styles.labelMore}
          onMouseEnter={() => setShowLabelPopover(true)}
          onMouseLeave={() => setShowLabelPopover(false)}
        >
          +{remainingCount}
          {showLabelPopover && (
            <div className={styles.labelPopover}>
              {remainingLabels.map((label) => (
                <span
                  key={label.id}
                  className={clsx(styles.label, styles.labelPopoverItem)}
                  title={label.text}
                >
                  {label.text}
                </span>
              ))}
            </div>
          )}
        </span>
      )}
    </div>
  );
};
